import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import streamstackLogo from '../stoken-logo.jpg';
import './LoginPage.css';

async function createUserDocIfNeeded(user) {
  const ref  = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await setDoc(ref, {
    uid:                  user.uid,
    email:                user.email,
    displayName:          user.displayName || '',
    totalStreams:         0,
    totalXp:              0,
    level:                1,
    taskStreamsEarned:     0,
    tasksCompleted:       0,
    dailyBonusDay:        0,
    dailyBonusStreak:     0,
    lastDailyBonusDate:   null,
    consecutiveLoginDays: 0,
    watchHours:           0,
    totalPayouts:         0,
    ownedCosmetics:       [],
    equippedCosmetics:    {},
    interactions:         0,
    hasAgreedToTerms:     false,
    createdAt:            serverTimestamp(),
    status:               'active',
  });
}

// In development/test mode the CAPTCHA is auto-bypassed so devs aren't blocked
const TEST_MODE = process.env.NODE_ENV === 'development' || process.env.REACT_APP_TEST_MODE === 'true';

// ── 8 challenge types ─────────────────────────────────────────────────────────
function makeChallenge() {
  const types = [
    'stream-math', 'addition', 'sequence', 'word-reverse',
    'odd-even', 'dot-count', 'app-fact', 'multiply',
  ];
  const type = types[Math.floor(Math.random() * types.length)];

  switch (type) {
    case 'stream-math': {
      const m = Math.floor(Math.random() * 10) + 3;
      return { type, input: 'number', answer: String(m),
        q: ['At ', <strong key="r">1 Stream/min</strong>, ', how many Streams after ', <strong key="m">{m} minutes</strong>, '?'] };
    }
    case 'addition': {
      const a = Math.floor(Math.random() * 21) + 5;
      const b = Math.floor(Math.random() * 21) + 5;
      return { type, input: 'number', answer: String(a + b),
        q: ['What is ', <strong key="eq">{a} + {b}</strong>, '?'] };
    }
    case 'sequence': {
      const s = Math.floor(Math.random() * 8) + 1;
      const d = Math.floor(Math.random() * 4) + 2;
      return { type, input: 'number', answer: String(s + d * 3),
        q: ['What comes next: ', <strong key="seq">{s}, {s+d}, {s+d*2}, ___</strong>, '?'] };
    }
    case 'word-reverse': {
      const words = ['STACK', 'EARN', 'WATCH', 'TOKEN', 'VAULT', 'AGENT'];
      const w = words[Math.floor(Math.random() * words.length)];
      return { type, input: 'text', answer: w.split('').reverse().join(''),
        q: ['Type this word ', <strong key="dir">backwards</strong>, ': ', <strong key="w">{w}</strong>] };
    }
    case 'odd-even': {
      const n = Math.floor(Math.random() * 48) + 2;
      return { type, input: 'choice', answer: n % 2 === 0 ? 'even' : 'odd', choices: ['Odd', 'Even'],
        q: ['Is ', <strong key="n">{n}</strong>, ' odd or even?'] };
    }
    case 'dot-count': {
      const c = Math.floor(Math.random() * 7) + 3;
      return { type, input: 'number', answer: String(c),
        q: ['How many 🟠 do you see? ', <span key="dots" className="captcha__dots">{'🟠'.repeat(c)}</span>] };
    }
    case 'app-fact': {
      return { type, input: 'number', answer: '1',
        q: ['How many Streams does ', <strong key="app">StreamStack</strong>, ' award per minute of watch time?'] };
    }
    case 'multiply': {
      const a = Math.floor(Math.random() * 8) + 2;
      const b = Math.floor(Math.random() * 5) + 2;
      return { type, input: 'number', answer: String(a * b),
        q: ['What is ', <strong key="eq">{a} × {b}</strong>, '?'] };
    }
    default:
      return makeChallenge();
  }
}

// ── StreamStack CAPTCHA ───────────────────────────────────────────────────────
function StreamCaptcha({ onVerified }) {
  const [challenge] = useState(() => makeChallenge());
  const [answer, setAnswer] = useState('');
  const [verified, setVerified] = useState(TEST_MODE);
  const [wrong, setWrong] = useState(false);

  React.useEffect(() => {
    if (TEST_MODE) onVerified();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const check = (val) => {
    const given    = (val !== undefined ? val : answer).trim().toUpperCase();
    const expected = challenge.answer.toUpperCase();
    if (given === expected) {
      setVerified(true);
      onVerified();
    } else {
      setWrong(true);
      setAnswer('');
      setTimeout(() => setWrong(false), 900);
    }
  };

  if (verified) {
    return (
      <div className="captcha captcha--verified">
        <span className="captcha__check-icon">✓</span>
        <span className="captcha__verified-text">
          {TEST_MODE ? 'Test mode — CAPTCHA bypassed' : "Verified — you're in!"}
        </span>
      </div>
    );
  }

  return (
    <div className={`captcha${wrong ? ' captcha--wrong' : ''}`}>
      <div className="captcha__header">
        <span className="captcha__icon">🔐</span>
        <span className="captcha__label">Verify you're human</span>
      </div>
      <p className="captcha__question">{challenge.q}</p>
      {challenge.input === 'choice' ? (
        <div className="captcha__choices">
          {challenge.choices.map(c => (
            <button key={c} type="button" className="btn btn--ghost captcha__choice-btn"
              onClick={() => check(c)}>
              {c}
            </button>
          ))}
        </div>
      ) : (
        <div className="captcha__row">
          <input
            className="login-page__input captcha__input"
            type={challenge.input === 'number' ? 'number' : 'text'}
            min="0"
            placeholder="Your answer…"
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && check()}
            autoComplete="off"
          />
          <button type="button" className="btn btn--primary captcha__btn" onClick={() => check()}>
            Verify
          </button>
        </div>
      )}
      {wrong && (
        <p className="login-page__error" style={{ marginTop: 8 }}>
          Incorrect — try again!
        </p>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const provider = new GoogleAuthProvider();

export default function LoginPage() {
  const [mode, setMode]           = useState('login'); // 'login' | 'signup'
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [tosAgreed, setTosAgreed] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setCaptchaOk(false); // reset captcha when switching tabs
    setTosAgreed(false);
  };

  const canSubmit = captchaOk && (mode === 'login' || tosAgreed);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const { user } = await createUserWithEmailAndPassword(auth, email, password);
        await createUserDocIfNeeded(user);
      }
    } catch (err) {
      setError(err.message.replace('Firebase: ', '').replace(/\(.*\)/, '').trim());
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (!captchaOk) return;
    setError('');
    setLoading(true);
    try {
      const { user } = await signInWithPopup(auth, provider);
      await createUserDocIfNeeded(user);
    } catch (err) {
      setError(err.message.replace('Firebase: ', '').trim());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="grid-bg" aria-hidden="true" />
      <div className="login-page__scanline" aria-hidden="true" />

      <div className="login-page__content">
        {/* Brand */}
        <div className="login-page__brand">
          <img src={streamstackLogo} alt="StreamStack" style={{ width: 80, height: 80, objectFit: 'contain' }} />
          <h1 className="login-page__wordmark">StreamStack</h1>
          <p className="login-page__tagline">Watch. Earn. Withdraw.</p>
        </div>

        {/* Card */}
        <div className="card login-page__card">
          {/* Tab toggle */}
          <div className="login-page__tabs">
            <button
              className={`login-page__tab${mode === 'login' ? ' login-page__tab--active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Sign In
            </button>
            <button
              className={`login-page__tab${mode === 'signup' ? ' login-page__tab--active' : ''}`}
              onClick={() => switchMode('signup')}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="login-page__form" noValidate>
            <div className="login-page__field">
              <label className="login-page__label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="login-page__input"
                placeholder="agent@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="login-page__field">
              <label className="login-page__label" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="login-page__input"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </div>

            {/* CAPTCHA */}
            <StreamCaptcha key={mode} onVerified={() => setCaptchaOk(true)} />

            {/* ToS checkbox — signup only */}
            {mode === 'signup' && (
              <label className="login-page__tos-row">
                <input
                  type="checkbox"
                  className="login-page__tos-check"
                  checked={tosAgreed}
                  onChange={e => setTosAgreed(e.target.checked)}
                />
                <span className="login-page__tos-text">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="login-page__tos-link">
                    Terms of Service
                  </Link>
                </span>
              </label>
            )}

            {error && <p className="login-page__error" role="alert">{error}</p>}

            <button
              type="submit"
              className="btn btn--primary btn--full"
              disabled={loading || !canSubmit}
            >
              {loading ? 'Loading…' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <div className="login-page__divider">
            <span>or</span>
          </div>

          <button
            className="btn btn--ghost btn--full login-page__google"
            onClick={handleGoogle}
            disabled={loading || !captchaOk}
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <p className="login-page__footer">
          By signing in you agree to our{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="login-page__tos-link">
            Terms of Service
          </Link>{' '}
          &amp; Privacy Policy.
        </p>
      </div>
    </div>
  );
}
